import React, { useEffect, useRef, useState } from "react";

import { BsPeople } from "react-icons/bs";
import { MdOutlineNordicWalking } from "react-icons/md";
import { IoLocationOutline } from "react-icons/io5";
import Select from "react-select";
import LocationInput from "../../../../components/ui/Form/LocationInput";
import { BiMinus, BiSolidCity } from "react-icons/bi";
import DatePicker from "rsuite/DatePicker";
import "rsuite/dist/rsuite.css";
import enGB from "date-fns/locale/en-GB";
import { ImSpinner2 } from "react-icons/im";
import { ToastContainer, toast } from "react-toastify";
import { AiOutlinePlus } from "react-icons/ai";
import Preloader from "../../../../components/ui/Preloader"; // Added Preloader import
import {
  // Added HiArrowLongRight import for button
  createBooking,
  fetchCities,
  fetchCity,
  fetchPasses,
} from "../../../../redux/slices/adminSlice";
import { useDispatch, useSelector } from "react-redux";
import { FaPersonWalkingDashedLineArrowRight } from "react-icons/fa6";
import PersonalDetailsForm from "../../../PersonalDetailsForm";

function AdminPriorityPassForm() {
  // Date Setup: Allow selection from today onwards (yesterday's date disables today)
  const minSelectableDate = new Date();
  minSelectableDate.setDate(minSelectableDate.getDate() - 1);
  // A function to disable dates earlier than the minimum date
  const disableDateBeforeMin = (date) => {
    return date < minSelectableDate;
  };

  const services = [
    { value: "Arrival Protocol", label: "Arrival Protocol" },
    { value: "Departure Protocol", label: "Departure Protocol" },
  ];

  // FORM FIELDS
  const [pickupLocation, setPickupLocation] = useState(null); // Holds geographic data (lat, lng)
  const pickupLocationRef = useRef(null);
  const [pickupLocationInput, setPickupLocationInput] = useState(""); // Holds display string of location
  const [pickupDate, setPickupDate] = useState(null);
  const [pickupTime, setPickupTime] = useState(null);
  const [selectedPass, setSelectedPass] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedProtocol, setSelectedProtocol] = useState(null); // Renamed from selectedService for consistency with FILE 1
  const [passengers, setPassengers] = useState(1); // Default to 1 passenger

  // Passenger Form Fields (for admin creating a booking for a customer)
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [flightNumber, setFlightNumber] = useState("");
  const [airline, setAirline] = useState("");

  // Redux store states and dispatch
  const { isLoading, token, cities, passes, bookingCurrency, bookingTotal } =
    useSelector((store) => store.admin);
  const dispatch = useDispatch();

  // Form submission loading state
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Fetch cities when token is available
  useEffect(() => {
    if (token) {
      dispatch(fetchCities({ token, type: "priority" })); // Fetches all cities
    }
  }, [dispatch, token]);

  // Fetch city details when selectedCity changes (might contain airport info etc.)
  // This also ensures LocationInput has the correct city context if needed.
  useEffect(() => {
    if (token && selectedCity) {
      dispatch(
        fetchCity({
          cityId: selectedCity?.value,
          token,
          type: "priority",
        })
      );
    }
  }, [dispatch, token, selectedCity]);

  // Fetch passes when token is available (assuming passes are global or filtered on client-side as per current admin slice)
  useEffect(() => {
    if (token) {
      dispatch(fetchPasses({ token })); // Fetches all passes
    }
  }, [dispatch, token]);

  // Format cities for the Select component
  const [citiesData, setCitiesData] = useState([]);
  useEffect(() => {
    let updatedCityData = [];
    cities?.forEach((city) => {
      updatedCityData.push({
        value: city?._id,
        label: city?.cityName,
      });
    });
    setCitiesData(updatedCityData);
  }, [cities]);

  // Format passes for the Select component
  const [passesData, setPassesData] = useState([]); // Renamed from passData for consistency
  useEffect(() => {
    let updatedPassData = [];
    passes?.forEach((pass) => {
      updatedPassData.push({
        value: pass?._id,
        label: pass?.name,
      });
    });
    setPassesData(updatedPassData);
  }, [passes]);

  // Title options for customer details
  const titleOptions = [
    { value: "Mr", label: "Mr" },
    { value: "Mrs", label: "Mrs" },
    { value: "Miss", label: "Miss" },
    { value: "Ms", label: "Ms" },
  ];

  // Helper function to increase passenger count
  const increasePassengers = () => {
    if (passengers < 10) {
      setPassengers(passengers + 1);
    } else {
      toast.info("You cannot select more than 10 passengers / passes!");
    }
  };

  // Helper function to decrease passenger count
  const decreasePassengers = () => {
    if (passengers > 1) {
      setPassengers(passengers - 1);
    }
  };

  // Handle creating a booking
  async function handleCreateBooking(e) {
    e.preventDefault();
    setIsRedirecting(true); // Start loading

    // Validate all required fields
    if (
      !selectedCity ||
      !pickupLocationInput ||
      !pickupDate ||
      !pickupTime ||
      !selectedProtocol ||
      !selectedPass ||
      passengers < 1 || // Ensure passengers is at least 1
      !firstName ||
      !lastName ||
      !email ||
      !phoneNumber ||
      !selectedTitle
    ) {
      toast.info(
        "Please fill in all required fields for booking and customer details."
      );
      setIsRedirecting(false); // Stop loading on validation error
      return;
    }

    try {
      await dispatch(
        createBooking({
          bookingType: "Priority",
          bookingDetails: {
            title: selectedTitle?.value,
            firstName: firstName,
            lastName: lastName,
            email: email,
            mobile: phoneNumber,
            bookingCurrency: bookingCurrency?._id, // Assuming bookingCurrency is ID
            bookingTotal: bookingTotal,
            passengers: passengers,
            airline: airline,
            flightNumber: flightNumber,
            passSelected: selectedPass?.value,
            citySelected: selectedCity?.value,
            protocolSelected: selectedProtocol?.value, // Consistent naming with FILE 1
            pickupAddress: pickupLocationInput,
            pickupCoordinates: {
              lat: pickupLocation?.latitude,
              lng: pickupLocation?.longitude,
            },
            pickupDate: pickupDate,
            pickupTime: pickupTime,
            // pickupTimeZone: timezone ?? null, // Removed as timezone isn't in admin slice context
          },
        })
      );
      toast.success("Booking created successfully!");
    } catch (error) {
      toast.error("Failed to create booking.");
      console.error("Booking creation error:", error);
    } finally {
      setIsRedirecting(false); // Stop loading after dispatch
    }
  }

  return (
    <>
      {(isLoading || isRedirecting) && <Preloader />}{" "}
      {/* Show preloader when loading or redirecting */}
      <form className="text-shuttlelaneBlack mt-10 flex flex-col gap-y-5">
        <ToastContainer toastClassName="text-sm" />

        {/* Booking Details Section */}
        <div className="mb-1">
          <p className="font-medium">Booking Details</p>
          <div className="h-1 w-[30px] rounded-xl bg-shuttlelanePurple"></div>
        </div>

        {/* Service and Pass Selection */}
        <div className="flex flex-col lg:flex-row gap-y-5 lg:items-center gap-x-4">
          {/* Protocol Service */}
          <div className="lg:w-[50%] w-full flex flex-col gap-y-1">
            <label htmlFor="protocolService" className="text-sm">
              Select Protocol Service
            </label>
            <div className="flex h-11 items-center border-[0.3px] bg-transparent focus:outline-none border-gray-400 py-2 px-2 gap-x-2 w-full rounded-lg">
              <div className="w-[5%]">
                <MdOutlineNordicWalking size={16} className="text-gray-500" />
              </div>

              <div className="w-[95%] text-shuttlelaneBlack text-sm relative z-[99]">
                <Select
                  value={selectedProtocol}
                  onChange={(value) => setSelectedProtocol(value)}
                  options={services}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused
                        ? "transparent"
                        : "transparent",
                      borderWidth: state.isFocused ? "0" : "0",
                      backgroundColor: "transparent",
                      position: "relative",
                      zIndex: 99,
                    }),
                    placeholder: (baseStyles, state) => ({
                      ...baseStyles,
                    }),
                    menuList: (baseStyles, state) => ({
                      ...baseStyles,
                    }),
                    input: (baseStyles, state) => ({
                      ...baseStyles,
                    }),
                  }}
                  placeholder="Select Protocol Service"
                />
              </div>
            </div>
          </div>

          {/* Pass */}
          <div className="lg:w-[50%] w-full flex flex-col gap-y-1">
            <label htmlFor="pass" className="text-sm">
              Select Pass
            </label>
            <div className="flex h-11 items-center border-[0.3px] bg-transparent focus:outline-none border-gray-400 py-2 px-2 gap-x-2 w-full rounded-lg">
              <div className="w-[5%]">
                <FaPersonWalkingDashedLineArrowRight
                  size={16}
                  className="text-gray-500"
                />
              </div>

              <div className="w-[95%] text-shuttlelaneBlack text-sm relative z-[80]">
                <Select
                  value={selectedPass}
                  onChange={(value) => setSelectedPass(value)}
                  options={passesData} // Used updated passesData state
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused
                        ? "transparent"
                        : "transparent",
                      borderWidth: state.isFocused ? "0" : "0",
                      backgroundColor: "transparent",
                      position: "relative",
                      zIndex: 80,
                    }),
                    placeholder: (baseStyles, state) => ({
                      ...baseStyles,
                    }),
                    menuList: (baseStyles, state) => ({
                      ...baseStyles,
                    }),
                    input: (baseStyles, state) => ({
                      ...baseStyles,
                    }),
                  }}
                  placeholder="Select Pass"
                />
              </div>
            </div>
          </div>
        </div>

        {/* City and Pickup Address */}
        <div className="flex flex-col lg:flex-row gap-y-5 lg:items-center gap-x-4">
          {/* City */}
          <div className="lg:w-[50%] w-full flex flex-col gap-y-1">
            <label htmlFor="city" className="text-sm">
              City
            </label>
            <div className="flex h-11 items-center border-[0.3px] bg-transparent focus:outline-none border-gray-400 py-2 px-2 gap-x-2 w-full rounded-lg">
              <div className="w-[5%]">
                <BiSolidCity size={16} className="text-gray-500" />
              </div>

              <div className="w-[95%] text-shuttlelaneBlack text-sm relative z-[70]">
                <Select
                  value={selectedCity}
                  onChange={(value) => setSelectedCity(value)}
                  options={citiesData}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused
                        ? "transparent"
                        : "transparent",
                      borderWidth: state.isFocused ? "0" : "0",
                      backgroundColor: "transparent",
                      position: "relative",
                      zIndex: 70,
                    }),
                    placeholder: (baseStyles, state) => ({
                      ...baseStyles,
                    }),
                    menuList: (baseStyles, state) => ({
                      ...baseStyles,
                    }),
                    input: (baseStyles, state) => ({
                      ...baseStyles,
                    }),
                  }}
                  placeholder="Select Destination City"
                />
              </div>
            </div>
          </div>

          {/* Pickup Address */}
          <div className="lg:w-[50%] w-full flex flex-col gap-y-1">
            <label htmlFor="pickupLocation" className="text-sm">
              Pickup Location
            </label>
            <div className="flex h-11 items-center border-[0.3px] bg-transparent focus:outline-none border-gray-400 py-2 px-2 gap-x-2 w-full rounded-lg">
              <div className="w-[5%]">
                <IoLocationOutline size={16} className="text-gray-700" />
              </div>

              <div className="w-[95%] text-shuttlelaneBlack relative z-[60]">
                <LocationInput
                  placeholder="Pickup From (Airport, Port, Address)" // Changed placeholder for clarity
                  setLocation={setPickupLocation}
                  location={pickupLocation}
                  locationRef={pickupLocationRef}
                  locationInput={pickupLocationInput}
                  setLocationInput={setPickupLocationInput}
                  city={selectedCity?.value}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Pickup Date and Time */}
        <div className="flex flex-col lg:flex-row gap-y-5 lg:items-center gap-x-4">
          <div className="w-full flex flex-col gap-y-1">
            <label htmlFor="pickupDate" className="text-sm">
              Pickup Date
            </label>
            <div className="flex h-11 items-center border-[0.3px] bg-transparent focus:outline-none border-gray-400 py-2 px-2 gap-x-2 w-full rounded-lg">
              <div className="w-full">
                <DatePicker
                  locale={enGB}
                  disabledDate={disableDateBeforeMin}
                  value={pickupDate}
                  appearance="subtle"
                  onChange={(date) => {
                    setPickupDate(date);
                  }}
                  placeholder="Pickup Date"
                  style={{
                    backgroundColor: "transparent",
                  }}
                  className="text-sm w-full bg-transparent text-shuttlelaneBlack"
                />
              </div>
            </div>
          </div>

          <div className="w-full flex flex-col gap-y-1">
            <label htmlFor="pickupTime" className="text-sm">
              Pickup Time
            </label>
            <div className="flex h-11 items-center border-[0.3px] bg-transparent focus:outline-none border-gray-400 py-2 px-2 gap-x-2 w-full rounded-lg">
              <div className="w-full">
                <DatePicker
                  format="HH:mm"
                  value={pickupTime}
                  appearance="subtle"
                  onChange={(time) => {
                    setPickupTime(time);
                  }}
                  placeholder="Pickup Time"
                  style={{
                    backgroundColor: "transparent",
                  }}
                  className="text-sm w-full bg-transparent text-shuttlelaneBlack"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Passengers Input with Increment/Decrement */}
        <div className="w-full flex flex-col gap-y-1">
          <label htmlFor="passengers" className="text-sm">
            Passengers
          </label>
          <div className="flex h-11 items-center border-[0.3px] bg-transparent focus:outline-none border-gray-400 py-2 px-2 gap-x-2 w-full rounded-lg">
            <div className="w-[5%]">
              <BsPeople size={16} className="text-gray-500" />
            </div>

            <div className="w-[95%] text-shuttlelaneBlack text-sm flex items-center justify-between">
              <button
                type="button"
                className="bg-gray-200 p-2 rounded-l-md"
                onClick={decreasePassengers}
              >
                <BiMinus size={16} />
              </button>
              <input
                type="tel" // Changed to tel for numeric input
                className="text-sm px-3 bg-transparent w-full focus:outline-none text-center"
                placeholder="Number of passengers"
                value={passengers}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (!isNaN(value) && value >= 1 && value <= 10) {
                    setPassengers(value);
                  } else if (e.target.value === "") {
                    setPassengers(""); // Allow empty string for clearing
                  } else {
                    toast.info(
                      "Number of passengers must be between 1 and 10."
                    );
                  }
                }}
              />
              <button
                type="button"
                className="bg-gray-200 p-2 rounded-r-md"
                onClick={increasePassengers}
              >
                <AiOutlinePlus size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Customer Details Section (from PersonalDetailsForm concept) */}
        <div className="mb-1 mt-5">
          <p className="font-medium">Customer Details</p>
          <div className="h-1 w-[30px] rounded-xl bg-shuttlelanePurple"></div>
        </div>

        <PersonalDetailsForm
          selectedTitle={selectedTitle}
          setSelectedTitle={setSelectedTitle}
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          email={email}
          setEmail={setEmail}
          hasFlightDetails={true}
          flightNumber={flightNumber}
          setFlightNumber={setFlightNumber}
          airline={airline}
          setAirline={setAirline}
        />

        <button
          onClick={(e) => handleCreateBooking(e)}
          className="lg:w-44 w-full h-13 p-3 border-[0.3px] focus:outline-none bg-shuttlelanePurple flex items-center justify-center text-white border-gray-400 rounded-lg"
          disabled={isLoading || isRedirecting} // Disable button when loading
        >
          {isLoading || isRedirecting ? (
            <ImSpinner2 size={21} className="text-white animate-spin" />
          ) : (
            "Create Booking"
          )}
        </button>
      </form>
    </>
  );
}

export default AdminPriorityPassForm;
